import { z } from 'zod';
import { isoTimestamp, pageQuery, severity, uuid } from './primitives.js';
/**
 * Uptime-Monitoring: MeshAlert prüft selbst, statt auf einen Webhook zu warten.
 *
 * Ein Monitor ist eine Quelle vom Typ `monitor`. Die Alarme, die er erzeugt,
 * laufen durch dieselbe Weiterleitung und Zustellung wie alles andere — es gibt
 * keinen zweiten Weg ans Telefon, der eigene Regeln hätte.
 */
export const MONITOR_PROTOCOLS = ['http', 'tcp', 'smtp', 'tls_expiry', 'dns'];
export const monitorProtocol = z.enum(MONITOR_PROTOCOLS);
export const MONITOR_PROTOCOL_LABEL = {
    http: 'HTTP/HTTPS',
    tcp: 'TCP-Port',
    smtp: 'SMTP',
    tls_expiry: 'TLS-Zertifikat',
    dns: 'DNS',
};
/** Was ein einzelner Protokolltyp braucht. */
export const httpCheck = z.object({
    protocol: z.literal('http'),
    url: z.url().max(2000),
    method: z.enum(['GET', 'HEAD', 'POST']).default('GET'),
    /** Alles ausserhalb gilt als Ausfall. */
    expect_status_min: z.number().int().min(100).max(599).default(200),
    expect_status_max: z.number().int().min(100).max(599).default(399),
    /** Optional: Antwort muss diesen Text enthalten. */
    expect_body_contains: z.string().max(500).nullish(),
    /** Selbstsignierte Zertifikate zulassen. Standard: nein. */
    allow_insecure_tls: z.boolean().default(false),
});
export const tcpCheck = z.object({
    protocol: z.literal('tcp'),
    host: z.string().min(1).max(255),
    port: z.number().int().min(1).max(65535),
});
export const smtpCheck = z.object({
    protocol: z.literal('smtp'),
    host: z.string().min(1).max(255),
    port: z.number().int().min(1).max(65535).default(25),
    /** Der Server muss mit 220 antworten, sonst nimmt er keine Mail an. */
    expect_banner_code: z.number().int().min(200).max(599).default(220),
});
export const tlsExpiryCheck = z.object({
    protocol: z.literal('tls_expiry'),
    host: z.string().min(1).max(255),
    port: z.number().int().min(1).max(65535).default(443),
    /** Alarm, sobald das Zertifikat in weniger als so vielen Tagen abläuft. */
    warn_days_before: z.number().int().min(1).max(365).default(14),
});
export const dnsCheck = z.object({
    protocol: z.literal('dns'),
    hostname: z.string().min(1).max(255),
    record_type: z.enum(['A', 'AAAA', 'MX', 'TXT', 'CNAME']).default('A'),
    /** Optional: einer der Werte muss dabei sein. */
    expect_contains: z.string().max(255).nullish(),
    /**
     * Optional gegen einen bestimmten Nameserver prüfen (`10.0.0.1` oder
     * `10.0.0.1:5353`). Ohne Angabe fragt der Standard-Resolver — der antwortet
     * aber aus seinem Zwischenspeicher und sagt damit nichts darüber aus, ob der
     * eigene autoritative Server noch läuft.
     */
    resolver: z
        .string()
        .max(64)
        .regex(/^[0-9a-fA-F.:]+(:\d{1,5})?$/, 'IP-Adresse, optional mit :Port')
        .nullish(),
});
export const monitorCheck = z.discriminatedUnion('protocol', [
    httpCheck,
    tcpCheck,
    smtpCheck,
    tlsExpiryCheck,
    dnsCheck,
]);
export const monitorConfig = z.object({
    check: monitorCheck,
    /** Wie oft geprüft wird. */
    interval_seconds: z.number().int().min(30).max(86400).default(60),
    timeout_ms: z.number().int().min(500).max(60000).default(10000),
    /**
     * Wie viele Fehlschläge hintereinander, bevor alarmiert wird. Ein einzelner
     * Aussetzer ist meistens das Netz und nicht der Dienst.
     */
    failure_threshold: z.number().int().min(1).max(10).default(2),
    /** Schweregrad des erzeugten Alarms. */
    severity: severity.default('critical'),
});
export const MONITOR_STATUSES = ['unknown', 'up', 'down'];
export const monitorStatus = z.enum(MONITOR_STATUSES);
export const monitorState = z.object({
    status: monitorStatus,
    /** Wie oft hintereinander die Prüfung zuletzt fehlschlug. */
    consecutive_failures: z.number().int(),
    last_checked_at: isoTimestamp.nullable(),
    last_ok_at: isoTimestamp.nullable(),
    last_error: z.string().nullable(),
    /** Antwortzeit der letzten erfolgreichen Prüfung. */
    last_duration_ms: z.number().int().nullable(),
    /** Nur bei tls_expiry gesetzt. */
    cert_expires_at: isoTimestamp.nullable(),
});
export const EMPTY_MONITOR_STATE = {
    status: 'unknown',
    consecutive_failures: 0,
    last_checked_at: null,
    last_ok_at: null,
    last_error: null,
    last_duration_ms: null,
    cert_expires_at: null,
};
export const createMonitorBody = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(2000).nullish(),
    monitor: monitorConfig,
});
export const updateMonitorBody = z
    .object({
    name: z.string().min(1).max(120),
    description: z.string().max(2000).nullable(),
    is_active: z.boolean(),
    monitor: monitorConfig,
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'Mindestens ein Feld erforderlich' });
/** Antwort auf „jetzt einmal prüfen", ohne den Zeitplan zu stören. */
export const monitorCheckResult = z.object({
    ok: z.boolean(),
    duration_ms: z.number().int(),
    detail: z.string(),
    cert_expires_at: isoTimestamp.nullish(),
});
/* ---------------------------------------------------------------- verlauf */
/**
 * Zeiträume des Verlaufs. Jeder hat eine feste Balkenbreite, nur „gesamt"
 * wählt sie nach der Spanne seit der ersten Prüfung — sonst wären es nach
 * einem Jahr 8760 Balken oder nach zwei Tagen ein einziger.
 */
export const MONITOR_HISTORY_RANGES = ['24h', '7d', '30d', 'all'];
export const monitorHistoryRange = z.enum(MONITOR_HISTORY_RANGES);
export const MONITOR_HISTORY_BUCKETS = ['1h', '6h', '1d', '1w'];
export const monitorHistoryBucketSize = z.enum(MONITOR_HISTORY_BUCKETS);
/** IANA-Zone. Tages- und Wochenbalken beginnen um Mitternacht dort, nicht in UTC. */
export const timeZone = z
    .string()
    .min(1)
    .max(64)
    .refine((tz) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}, 'Unbekannte Zeitzone');
export const monitorHistoryQuery = z.object({
    range: monitorHistoryRange.default('24h'),
    tz: timeZone.default('Europe/Berlin'),
});
/**
 * Ein Balken. Jede geplante Prüfung zählt in genau einen davon; `checks = 0`
 * heisst „nicht geprüft" (pausiert oder noch nicht angelegt) und ist etwas
 * anderes als „alles erreichbar".
 */
export const monitorHistoryBucket = z.object({
    start: isoTimestamp,
    end: isoTimestamp,
    checks: z.number().int(),
    failures: z.number().int(),
    /** Nur über erfolgreiche Prüfungen: ein Timeout ist keine Antwortzeit. */
    avg_duration_ms: z.number().int().nullable(),
    max_duration_ms: z.number().int().nullable(),
});
export const monitorHistoryTotals = z.object({
    checks: z.number().int(),
    failures: z.number().int(),
    /** Anteil erfolgreicher Prüfungen, 0 bis 1. `null` ohne eine einzige Prüfung. */
    uptime: z.number().nullable(),
    avg_duration_ms: z.number().int().nullable(),
});
export const monitorHistory = z.object({
    monitor_id: uuid,
    range: monitorHistoryRange,
    bucket: monitorHistoryBucketSize,
    from: isoTimestamp,
    to: isoTimestamp,
    buckets: z.array(monitorHistoryBucket),
    totals: monitorHistoryTotals,
});
/** Eine einzelne geplante Prüfung, so wie der Scheduler sie ausgeführt hat. */
export const monitorCheckRecord = z.object({
    id: uuid,
    checked_at: isoTimestamp,
    ok: z.boolean(),
    duration_ms: z.number().int(),
    detail: z.string(),
});
export const listMonitorChecksQuery = pageQuery.extend({
    /** `false` zeigt nur Fehlschläge. */
    ok: z.stringbool().optional(),
});
export const monitorSummary = z.object({
    id: uuid,
    key: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    is_active: z.boolean(),
    monitor: monitorConfig,
    state: monitorState,
    next_check_at: isoTimestamp.nullable(),
    created_at: isoTimestamp,
});
//# sourceMappingURL=monitors.js.map